import React, { useEffect, useState, useMemo } from 'react';
import './AnimatedCircularProgress.css';

export const AnimatedCircularProgress = React.memo(({
  score,
  label = "점수",
  size = 120,
  strokeWidth = 12,
  delay = 500,
  color = "#4285F4",
  fontSize = "3xl",
  variant = "modern" // "modern"
}) => {
  const [progress, setProgress] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  // 점수에 따른 색상과 라벨 결정 (-100 ~ +100)
  const getScoreColor = (score) => {
    if (score >= 70) return '#22c55e'; // 녹색 (매우 긍정)
    if (score >= 40) return '#3b82f6'; // 파란색 (긍정)
    if (score >= -40) return '#f59e0b'; // 노란색 (중립)
    if (score >= -70) return '#f97316'; // 주황색 (부정)
    return '#ef4444'; // 빨간색 (매우 부정)
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return '매우 긍정';
    if (score >= 40) return '긍정';
    if (score >= -40) return '중립';
    if (score >= -70) return '부정';
    return '매우 부정';
  };

  // 진행률 계산: 음수는 왼쪽으로, 양수는 오른쪽으로
  const getProgressValues = (score) => {
    const absScore = Math.abs(score);
    const normalizedProgress = (absScore / 100) * 100;
    return {
      progress: normalizedProgress,
      isNegative: score < 0,
      absScore: absScore
    };
  };

  const progressValues = useMemo(() => getProgressValues(score), [score]);
  const currentColor = useMemo(() => color === "#4285F4" ? getScoreColor(score) : color, [color, score]);
  const currentLabel = useMemo(() => label === "점수" ? getScoreLabel(score) : label, [label, score]);

  useEffect(() => {
    // 이미 애니메이션이 실행된 경우 즉시 최종 값으로 설정
    if (hasAnimated) {
      setDisplayScore(score);
      setProgress(progressValues.progress);
      return;
    }

    const timer = setTimeout(() => {
      const duration = 1500;
      const startTime = Date.now();
      const targetProgress = progressValues.progress;

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const animationProgress = Math.min(elapsed / duration, 1);

        const easeOut = 1 - Math.pow(1 - animationProgress, 3);
        const currentScore = Math.round(score * easeOut);
        const currentProgress = targetProgress * easeOut;

        setDisplayScore(currentScore);
        setProgress(currentProgress);

        if (animationProgress < 1) {
          requestAnimationFrame(animate);
        } else {
          setHasAnimated(true);
        }
      };

      animate();
    }, delay);

    return () => clearTimeout(timer);
  }, [score, delay, progressValues.progress, hasAnimated]);

  // 모던 스타일
  return (
    <div className="modern-score-container">
      <div className="modern-score-main">
        <div className="modern-score-number" style={{ color: currentColor }}>
          {displayScore > 0 ? '+' : ''}{displayScore}
        </div>
        <div className="modern-score-label"  style={{ color: currentColor }}>{currentLabel}</div>
      </div>
      <div className="modern-score-visual">
        <div className="modern-score-segments">
          {/* 음수 세그먼트 (왼쪽) */}
          {[...Array(5)].map((_, i) => {
            const segmentThreshold = (i + 1) * 20;
            const isActive = !progressValues.isNegative ? false : progress >= segmentThreshold;
            const isPartial = !progressValues.isNegative ? false : progress > (i * 20) && progress < segmentThreshold;
            const partialHeight = isPartial ? ((progress - (i * 20)) / 20) * 100 : 100;
            
            return (
              <div
                key={`neg-${i}`}
                className={`modern-score-segment negative ${isActive ? 'active shimmer' : ''}`}
                style={{
                  backgroundColor: !progressValues.isNegative ? '#e5e7eb' : isActive ? currentColor : '#e5e7eb',
                  animationDelay: `${(4 - i) * 100}ms`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {isPartial && (
                  <div
                    className="segment-partial-fill"
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: `${partialHeight}%`,
                      backgroundColor: currentColor,
                      transition: 'height 0.3s ease'
                    }}
                  />
                )}
              </div>
            );
          }).reverse()}
          
          {/* 중앙 마커 */}
          <div className="modern-score-center-marker" />
          
          {/* 양수 세그먼트 (오른쪽) */}
          {[...Array(5)].map((_, i) => {
            const segmentThreshold = (i + 1) * 20;
            const isActive = progressValues.isNegative ? false : progress >= segmentThreshold;
            const isPartial = progressValues.isNegative ? false : progress > (i * 20) && progress < segmentThreshold;
            const partialHeight = isPartial ? ((progress - (i * 20)) / 20) * 100 : 100;
            
            return (
              <div
                key={`pos-${i}`}
                className={`modern-score-segment positive ${isActive ? 'active shimmer' : ''}`}
                style={{
                  backgroundColor: progressValues.isNegative ? '#e5e7eb' : isActive ? currentColor : '#e5e7eb',
                  animationDelay: `${i * 100}ms`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {isPartial && (
                  <div
                    className="segment-partial-fill"
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: `${partialHeight}%`,
                      backgroundColor: currentColor,
                      transition: 'height 0.3s ease'
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export const AnimatedSentimentScore = React.memo(({ score, delay = 500 }) => {
  const [progress, setProgress] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  const getColor = (score) => {
    if (score > 0) return "#10b981";
    if (score < 0) return "#ef4444";
    return "#f59e0b";
  };

  const getTextColorClass = (score) => {
    if (score > 0) return "text-green-500";
    if (score < 0) return "text-red-500";
    return "text-yellow-600";
  };

  const getLabel = (score) => {
    if (score > 0) return "긍정적";
    if (score < 0) return "부정적";
    return "중립적";
  };

  const normalizedScore = Math.abs(score);
  const progressScore = useMemo(() => Math.min(normalizedScore, 100), [normalizedScore]);
  const size = 64;
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  useEffect(() => {
    // 이미 애니메이션이 실행된 경우 즉시 최종 값으로 설정
    if (hasAnimated) {
      setDisplayScore(score);
      setProgress(progressScore);
      return;
    }

    const timer = setTimeout(() => {
      const duration = 1500;
      const startTime = Date.now();
      const targetProgressScore = progressScore;

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const animationProgress = Math.min(elapsed / duration, 1);

        const easeOut = 1 - Math.pow(1 - animationProgress, 3);
        const currentScore = Math.round(score * easeOut);
        const currentProgress = targetProgressScore * easeOut;

        setDisplayScore(currentScore);
        setProgress(currentProgress);

        if (animationProgress < 1) {
          requestAnimationFrame(animate);
        } else {
          setHasAnimated(true);
        }
      };

      animate();
    }, delay);

    return () => clearTimeout(timer);
  }, [score, delay, progressScore, hasAnimated]);

  return (
    <div className="flex items-center">
      <div 
        className="relative flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <svg
          className="absolute"
          width={size}
          height={size}
          style={{ transform: 'rotate(-90deg)' }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#f0f0f0"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={getColor(score)}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          />
        </svg>
        
        {/* 정확한 중앙 정렬 */}
        <div 
          className="absolute flex items-center justify-center"
          style={{ 
            width: size - strokeWidth * 2, 
            height: size - strokeWidth * 2,
            left: strokeWidth,
            top: strokeWidth
          }}
        >
          <span className={`text-lg font-bold ${getTextColorClass(score)} leading-none`}>
            {displayScore > 0 ? '+' : ''}{displayScore}
          </span>
        </div>
      </div>
      <div className="ml-3">
        <div className={`text-xs font-bold ${getTextColorClass(score)}`}>
          {getLabel(score)}
        </div>
      </div>
    </div>
  );
});
